import { useCallback, useEffect, useState } from 'react';
import api from './api';
import { fill } from './pageCopy';

export { fill };

/** Student's guided next steps (`/api/next-steps/me`) + a refresh after RSVP / manual completion. */
export function useNextSteps() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(() => api.get('/api/next-steps/me').then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false)), []);
  useEffect(() => { refresh(); }, [refresh]);
  return { data, loading, refresh };
}

export function classWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const day = d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
}

export function classEnd(ev) {
  return new Date(new Date(ev.start_at).getTime() + (ev.duration_minutes || 30) * 60_000);
}

export function stepVars(data) {
  return { ...(data?.vars || {}), class_when: data?.class ? classWhen(data.class.event.start_at) : '' };
}

export function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/** Join target for the hero / strip: in-app Zoom room path or the pasted (normalised) link. */
export function joinHref(cls) {
  const u = String(cls?.join_url || '').trim();
  if (!u) return '';
  if (u.startsWith('/')) return u;
  if (/^[a-z][a-z0-9+.-]*:/i.test(u)) return u;
  return `https://${u}`;
}

function absoluteJoin(cls) {
  const h = joinHref(cls);
  return h ? (h.startsWith('/') ? `${window.location.origin}${h}` : h) : `${window.location.origin}/group-classes`;
}

const icsStamp = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');

export function googleCalendarUrl(cls) {
  const ev = cls.event;
  const start = new Date(ev.start_at);
  const p = new URLSearchParams({
    action: 'TEMPLATE', text: ev.title, dates: `${icsStamp(start)}/${icsStamp(classEnd(ev))}`,
    details: `${ev.description || ''}\n\nJoin: ${absoluteJoin(cls)}`.trim(), location: absoluteJoin(cls),
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

export function downloadIcs(cls) {
  const ev = cls.event;
  const start = new Date(ev.start_at);
  const join = absoluteJoin(cls);
  const desc = `${(ev.description || '').replace(/\n/g, '\\n')}\\n\\nJoin: ${join}`;
  const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//CELPIPMax//Group class//EN', 'BEGIN:VEVENT', `UID:${ev.id}@celpipmax`,
    `DTSTAMP:${icsStamp(new Date())}`, `DTSTART:${icsStamp(start)}`, `DTEND:${icsStamp(classEnd(ev))}`, `SUMMARY:${ev.title}`,
    `DESCRIPTION:${desc}`, `LOCATION:${join}`, `URL:${join}`, 'BEGIN:VALARM', 'TRIGGER:-PT30M', 'ACTION:DISPLAY', `DESCRIPTION:${ev.title} starts in 30 minutes`,
    'END:VALARM', 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = `${ev.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export async function saveSeat(cls) {
  await api.post(`/api/events/${cls.event.id}/rsvp`);
  try { downloadIcs(cls); } catch (e) { /* calendar download is best-effort */ }
}
